import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useClaims } from "@/hooks/use-claims";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";
import { Loader2 } from "lucide-react";
import { ClaimPipelineSelect } from "./ClaimPipelineSelect";

interface ManualEntryFormProps {
    onSuccess: (claimId: number) => void;
}

export function ManualEntryForm({ onSuccess }: ManualEntryFormProps) {
    const { createClaimAsync, isCreating } = useClaims();
    const { toast } = useToast();
    const [pipelineId, setPipelineId] = useState<number | undefined>();
    const [manualForm, setManualForm] = useState({
        insuredName: "",
        insuredPhone: "",
        insuredPhone2: "",
        insuredEmail: "",
        insuredEmail2: "",
        mailingAddress: "",
        propertyAddress: "",
        claimNumber: "",
        policyNumber: "",
        insuranceCarrier: "",
        contractor: "",
        internalFileNumber: "",
        dateOfLoss: "",
        lossType: "",
    });

    const handleManualSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const newClaim = await createClaimAsync({
                insuredName: manualForm.insuredName,
                insuredPhone: manualForm.insuredPhone || null,
                insuredPhone2: manualForm.insuredPhone2 || null,
                insuredEmail: manualForm.insuredEmail || null,
                insuredEmail2: manualForm.insuredEmail2 || null,
                mailingAddress: manualForm.mailingAddress || null,
                propertyAddress: manualForm.propertyAddress || null,
                claimNumber: manualForm.claimNumber,
                policyNumber: manualForm.policyNumber || null,
                insuranceCarrier: manualForm.insuranceCarrier || null,
                contractor: manualForm.contractor || null,
                internalFileNumber: manualForm.internalFileNumber || null,
                dateOfLoss: manualForm.dateOfLoss || null,
                lossType: manualForm.lossType || null,
                pipelineId,
                claimStatusTemplateId: undefined
            });

            if (newClaim?.id) {
                toast({ title: "Claim created successfully" });
                onSuccess(newClaim.id);
            } else {
                throw new Error("Created claim has no ID");
            }
        } catch (error) {
            toast({
                title: "Failed to create claim",
                description: getErrorMessage(error),
                variant: "destructive",
            });
        }
    };

    return (
        <form onSubmit={handleManualSubmit} className="max-h-[60dvh] md:max-h-[60vh] overflow-y-auto px-1">
            <div className="space-y-4">
            <ClaimPipelineSelect value={pipelineId} onChange={setPipelineId} disabled={isCreating} />
            {/* Insured Information */}
            <div className="space-y-3">
                <div className="space-y-2">
                    <Label htmlFor="insuredName">Insured Name *</Label>
                    <Input
                        id="insuredName"
                        value={manualForm.insuredName}
                        onChange={(e) => setManualForm({ ...manualForm, insuredName: e.target.value })}
                        placeholder="John Doe"
                        required
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label htmlFor="insuredPhone">Phone</Label>
                        <Input
                            id="insuredPhone"
                            type="tel"
                            value={manualForm.insuredPhone}
                            onChange={(e) => setManualForm({ ...manualForm, insuredPhone: e.target.value })}
                            placeholder="(555) 123-4567"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="insuredPhone2">Phone 2</Label>
                        <Input
                            id="insuredPhone2"
                            type="tel"
                            value={manualForm.insuredPhone2}
                            onChange={(e) => setManualForm({ ...manualForm, insuredPhone2: e.target.value })}
                            placeholder="(555) 987-6543"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label htmlFor="insuredEmail">Email</Label>
                        <Input
                            id="insuredEmail"
                            type="email"
                            value={manualForm.insuredEmail}
                            onChange={(e) => setManualForm({ ...manualForm, insuredEmail: e.target.value })}
                            placeholder="john@example.com"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="insuredEmail2">Email 2</Label>
                        <Input
                            id="insuredEmail2"
                            type="email"
                            value={manualForm.insuredEmail2}
                            onChange={(e) => setManualForm({ ...manualForm, insuredEmail2: e.target.value })}
                            placeholder="john2@example.com"
                        />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="mailingAddress">Mailing Address</Label>
                    <Input
                        id="mailingAddress"
                        value={manualForm.mailingAddress}
                        onChange={(e) => setManualForm({ ...manualForm, mailingAddress: e.target.value })}
                        placeholder="If different from loss address"
                    />
                </div>
            </div>

            {/* Loss Information */}
            <div className="space-y-3">
                <div className="space-y-2">
                    <Label htmlFor="propertyAddress">Loss Address</Label>
                    <Input
                        id="propertyAddress"
                        value={manualForm.propertyAddress}
                        onChange={(e) => setManualForm({ ...manualForm, propertyAddress: e.target.value })}
                        placeholder="123 Main St, City, ST 12345"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label htmlFor="dateOfLoss">Date of Loss</Label>
                        <Input
                            id="dateOfLoss"
                            type="date"
                            value={manualForm.dateOfLoss}
                            onChange={(e) => setManualForm({ ...manualForm, dateOfLoss: e.target.value })}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="lossType">Cause of Loss</Label>
                        <Input
                            id="lossType"
                            value={manualForm.lossType}
                            onChange={(e) => setManualForm({ ...manualForm, lossType: e.target.value })}
                            placeholder="Wind, Hail, Water, etc."
                        />
                    </div>
                </div>
            </div>

            {/* Claim Information */}
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label htmlFor="claimNumber">Claim Number *</Label>
                        <Input
                            id="claimNumber"
                            value={manualForm.claimNumber}
                            onChange={(e) => setManualForm({ ...manualForm, claimNumber: e.target.value })}
                            placeholder="CLM-2026-001"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="policyNumber">Policy Number</Label>
                        <Input
                            id="policyNumber"
                            value={manualForm.policyNumber}
                            onChange={(e) => setManualForm({ ...manualForm, policyNumber: e.target.value })}
                            placeholder="POL-12345"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label htmlFor="insuranceCarrier">Insurance Company</Label>
                        <Input
                            id="insuranceCarrier"
                            value={manualForm.insuranceCarrier}
                            onChange={(e) => setManualForm({ ...manualForm, insuranceCarrier: e.target.value })}
                            placeholder="State Farm, Allstate, etc."
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="contractor">Contractor</Label>
                        <Input
                            id="contractor"
                            value={manualForm.contractor}
                            onChange={(e) => setManualForm({ ...manualForm, contractor: e.target.value })}
                            placeholder="Company or referral source"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label htmlFor="internalFileNumber">Internal File #</Label>
                        <Input
                            id="internalFileNumber"
                            value={manualForm.internalFileNumber}
                            onChange={(e) => setManualForm({ ...manualForm, internalFileNumber: e.target.value })}
                            placeholder="Optional"
                        />
                    </div>
                </div>
            </div>

            <Button type="submit" disabled={isCreating} className="w-full">
                {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create Claim
            </Button>
            </div>
        </form>
    );
}
